
import * as math from '../utils/math';


// A flight from the current camera to a target definition. The start
// is the camera's definition measured from the target's altitude
// reference, so both ends interpolate in the same terms.
var MapTrajectory = function(map, camera, target, options) {
    this.map = map;
    this.p1 = camera.withAltitudeRef(target.altitudeRef);
    this.p2 = copyDefinition(target);
    this.op2 = copyDefinition(target);

    this.p1.yaw = normalizeYaw(this.p1.yaw);
    this.p2.yaw = normalizeYaw(this.p2.yaw);

    this.pp1 = copyDefinition(this.p1);
    this.pp2 = copyDefinition(this.p2);

    this.mode = options['mode'] || 'auto';
    this.pv = options['pv'] || 0.15;
    this.maxHeight = options['maxHeight'] || 1000000000;
    this.minDuration = options['minDuration'] || 0;
    this.maxDuration = options['maxDuration'] || 10000;
    this.samplePeriod = options['samplePeriod'] || 10;
    this.fade = options['fade'] || 'none';
    this.fadePower = options['fadePower'] || 1;
    this.yawInterpolation = options['yawInterpolation'] || 'shortest';

    if (!this.map.getNavigationSrs().isProjected()) {
        this.geodesic = this.map.measure.getGeodesic();
    }

    //get distance and azimut
    var res = this.map.measure.getDistance(
        this.coords(this.pp1), this.coords(this.pp2));
    this.distance = res[0];
    this.azimuth = (res[1] + 90) % 360;
    this.azimuth = (this.azimuth < 0) ? (360 + this.azimuth) : this.azimuth;

    if (!this.map.getNavigationSrs().isProjected()) {
        res = this.geodesic.Inverse(
            this.pp1.center[1], this.pp1.center[0],
            this.pp2.center[1], this.pp2.center[0]);
        this.geoAzimuth = res.azi1;
        this.geoDistance = res.s12;
        this.azimuth = this.geoAzimuth % 360;
        this.azimuth = (this.azimuth < 0) ? (360 + this.azimuth) : this.azimuth;
    }

    this.detectMode();
    this.detectDuration();
    this.detectFlightHeight(options['height']);
};


// The navigation coordinates of a definition, with its altitude.
MapTrajectory.prototype.coords = function(definition) {
    return [definition.center[0], definition.center[1], definition.altitude];
};


MapTrajectory.prototype.detectFlightHeight = function(flightHeight) {
    if (this.mode == 'ballistic') {
        this.flightHeight = Math.max(this.pp1.altitude, this.pp2.altitude);
        this.flightHeight += flightHeight || (this.distance * 0.5);
        this.flightHeight = Math.min(this.flightHeight, this.maxHeight);
        this.flightHeight -= Math.max(this.pp1.altitude, this.pp2.altitude);
    }
};


MapTrajectory.prototype.detectMode = function() {
    if (this.mode == 'auto') {
        this.mode = (this.distance > 2000) ? 'ballistic' : 'direct';
    }
};


MapTrajectory.prototype.detectDuration = function() {
    this.duration = 0;
    this.headingDuration = 1000;

    if (this.distance < 500) {
        this.duration = 1000;
    } else if (this.distance < 2000) {
        this.duration = 2000;
    } else {
        this.duration = this.distance / 100;

        if (this.duration < 300) {
            this.duration = 3000;
        } else {
            this.headingDuration = 1500;
        }

        if (this.duration < 6000) {
            this.duration = 6000;
        }

        if (this.duration > 10000) {
            this.duration = 10000;
        }

        if (this.mode != 'direct') {
            this.duration *= 1.8;
            this.headingDuration *= 1.8;
        }
    }

    if (this.mode != 'direct') {
        var minDuration = 3 * this.headingDuration;
        this.duration = Math.max(this.duration, minDuration);

        if (this.maxDuration < minDuration) {
            this.duration = this.maxDuration;
            this.headingDuration = this.maxDuration / 3;
        }
    }

    this.duration = Math.min(this.duration, this.maxDuration);
    this.duration = Math.max(this.duration, this.minDuration);

    // headingDurationStart / headingDuration are independent: start and end
    // orientation phases may differ.  When departing from near-nadir, yaw
    // rotation is invisible so we shrink the departure phase only and trim
    // duration by the same amount.  pitch: 0=horizontal, -90=nadir.
    this.headingDurationStart = this.headingDuration;
    if (this.mode != 'direct') {
        var startPitch = this.pp1.pitch;
        if (startPitch < -60) {
            var nadirFactor = Math.max(0, (startPitch + 90) / 30); // 1 at -60°, 0 at -90°
            var headingSaved = this.headingDuration * (1 - nadirFactor);
            this.headingDurationStart = Math.round(this.headingDuration * nadirFactor);
            this.duration = Math.max(this.minDuration, Math.round(this.duration - headingSaved));
        }
    }

    // When the flight distance is small relative to the view extent, the
    // linear travel phase is barely perceptible (the scene hardly moves).
    // Scale that phase down proportionally so the transition doesn't waste
    // time on near-stationary travel.  Arrival orientation (headingDuration)
    // is intentionally left unchanged — it carries the semantic payload.
    // A floor of 0.2 prevents the linear phase from collapsing to zero.
    // We use the SMALLER of the two extents as the reference: the criterion
    // is whether the distance fits inside the tighter viewport — using the
    // mean would incorrectly trigger on large-scale-change flights where one
    // extent is huge (e.g. zooming from regional to continental scale).
    if (this.mode != 'direct') {
        var e1 = this.pp1.viewExtent;
        var e2 = this.pp2.viewExtent;
        if (e1 > 0 && e2 > 0) {
            var minExtent = Math.min(e1, e2);
            var distRatio = this.distance / minExtent;
            if (distRatio < 1.0) {
                var arrivalPhase = this.headingDuration;
                var linearPhase = this.duration - this.headingDurationStart - arrivalPhase;
                if (linearPhase > 0) {
                    var scaledLinear = Math.round(linearPhase * Math.max(distRatio, 0.2));
                    this.duration = Math.max(this.minDuration,
                        this.headingDurationStart + scaledLinear + arrivalPhase);
                }
            }
        }
    }
};


// The camera definitions of the flight, one per sample period, ending
// with the target itself. `finalPhaseSample` on the array is the index
// where the arrival orientation phase begins.
MapTrajectory.prototype.generate = function() {
    var samples = new Array(Math.ceil(this.duration / this.samplePeriod) + 1);
    var index = 0;

    for (var time = 0; time <= this.duration; time += this.samplePeriod) {
        var factor = time / this.duration;

        var p = copyDefinition(this.pp1), x, coords, orientation;

        if (this.mode == 'direct') {

            x = factor;

            switch(this.fade) {
            case 'in':
                switch(this.fadePower) {
                case 1: factor = x*x; break;
                case 2: factor = x*x*x; break;
                case 3: factor = x*x*x*x; break;
                case 4: factor = x*x*x*x*x; break;
                case 5: factor = x*x*x*x*x*x; break;
                case 6: factor = x*x*x*x*x*x*x; break;
                }
                break;

            case 'out':
                x = 1 - x;
                switch(this.fadePower) {
                case 1: factor = 1 - (x*x); break;
                case 2: factor = 1 - (x*x*x); break;
                case 3: factor = 1 - (x*x*x*x); break;
                case 4: factor = 1 - (x*x*x*x*x); break;
                case 5: factor = 1 - (x*x*x*x*x*x); break;
                case 6: factor = 1 - (x*x*x*x*x*x*x); break;
                }
                break;

            case 'inout':
                switch(this.fadePower) {
                case 1: factor = x*x*(3 - 2*x); break;
                case 2: factor = x*x*x * (x * (6*x - 15) + 10); break;
                case 3: factor = x*x*(3 - 2*x); x = factor; factor = x*x*(3 - 2*x); break;
                case 4: factor = x*x*x * (x * (6*x - 15) + 10); x = factor; factor = x*x*x * (x * (6*x - 15) + 10); break;
                case 5: factor = x*x*(3 - 2*x); x = factor; factor = x*x*(3 - 2*x); x = factor; factor = x*x*(3 - 2*x); break;
                case 6: factor = x*x*x * (x * (6*x - 15) + 10); x = factor; factor = x*x*x * (x * (6*x - 15) + 10); x = factor; factor = x*x*x * (x * (6*x - 15) + 10); break;
                }
                break;
            }

            coords = this.getInterpolatedCoords(factor);
            p.center = [coords[0], coords[1]];
            p.altitude = this.getInterpolatedHeight(factor);

            var o1 = this.orientation(this.pp1);
            var o2 = this.orientation(this.pp2);

            orientation = this.getInterpolatedOrinetation(o1, o2, factor);
            p.yaw = orientation[0];
            p.pitch = orientation[1];
            p.roll = orientation[2];
            p.fovy = this.getInterpolatedFov(factor);
            p.viewExtent = this.getInterpolatedViewExtent(factor);

            samples[index] = p;
            index++;
        } else {

            //http://en.wikipedia.org/wiki/Smoothstep
            x = factor;
            factor =  x*x*(3 - 2*x);
            x = factor;
            factor =  x*x*(3 - 2*x);

            //factor2 includes slow start and end of flight
            var factor2 =  this.getSmoothFactor(time);

            if (this.mode == 'piha') {

                // the center advances by a raised-cosine velocity profile
                // and the altitude follows that velocity, so the terrain
                // passes under the eye at a steady perceived speed
                var progress = time / this.duration;
                var distanceFactor = progress
                    - Math.sin(2 * Math.PI * progress) / (2 * Math.PI);
                var speedHeight = this.distance
                    / ((this.duration*0.001) * this.pv
                        * Math.tan(math.radians(this.pp1.fovy) * 0.5))
                    * (1 - Math.cos(2 * Math.PI * progress));

                coords = this.getInterpolatedCoords(distanceFactor);

                p.center = [coords[0], coords[1]];
                p.altitude = speedHeight
                    + this.getInterpolatedHeight(progress);
            } else {

                coords = this.getInterpolatedCoords(factor2);

                p.center = [coords[0], coords[1]];
                p.altitude = this.getSineHeight(factor);
            }

            if (coords[2] != null) { //used for correction in planet mode
                this.azimuth = -coords[2];
            }

            orientation = this.getFlightOrienation(time);
            p.yaw = orientation[0];
            p.pitch = orientation[1];
            p.roll = orientation[2];
            p.fovy = this.getInterpolatedFov(factor);
            p.viewExtent = this.getInterpolatedViewExtent(factor);

            samples[index] = p;
            index++;
        }
    }

    samples[index] = copyDefinition(this.op2);

    samples.finalPhaseSample = Math.max(0, samples.length - 1 - Math.round(this.headingDuration / this.samplePeriod));

    return samples;
};


// The three angles of a definition.
MapTrajectory.prototype.orientation = function(definition) {
    return [definition.yaw, definition.pitch, definition.roll];
};


// The center at a fraction of the flight; on a planet with the change
// of azimuth along the geodesic as the third component.
MapTrajectory.prototype.getInterpolatedCoords = function(factor) {
    var c1 = this.pp1.center;
    var c2 = this.pp2.center;

    if (!this.map.getNavigationSrs().isProjected()) {
        var res = this.geodesic.Direct(c1[1], c1[0], this.geoAzimuth, this.geoDistance * factor);

        var azimut = res.azi1 - res.azi2;

        //var azimut = (azimut - 90) % 360;
        azimut = (this.azimuth < 0) ? (360 + azimut) : azimut;

        //azimut = this.azimuth;


        return [ res.lon2, res.lat2, azimut];

    } else {
        return [ c1[0] + (c2[0] - c1[0]) * factor,
            c1[1] + (c2[1] - c1[1]) * factor ];
    }
};


MapTrajectory.prototype.getInterpolatedOrinetation = function(o1, o2, factor) {
    var od1 = o2[0] - o1[0];
    var od2 = o2[1] - o1[1];
    var od3 = o2[2] - o1[2];

    if (this.yawInterpolation == 'shortest' || this.yawInterpolation == 'longest') {
        if (Math.abs(od1) > 180) {
            if (od1 > 0) {
                od1 = -(360 - od1);
            } else {
                od1 = 360 - Math.abs(od1);
            }
        }
    }

    if (this.yawInterpolation == 'longest') {
        if (od1 < 0) {
            od1 = 360 + od1;
        } else {
            od1 = (-360) + od1;
        }
    }

    return [ o1[0] + od1 * factor,
        o1[1] + od2 * factor,
        o1[2] + od3 * factor ];
};


MapTrajectory.prototype.getInterpolatedFov = function(factor) {
    var f1 = this.pp1.fovy;
    var f2 = this.pp2.fovy;
    return f1 + (f2 - f1) * factor;
};


MapTrajectory.prototype.getInterpolatedViewExtent = function(factor) {
    var v1 = this.pp1.viewExtent;
    var v2 = this.pp2.viewExtent;
    return v1 + (v2 - v1) * factor;
};


MapTrajectory.prototype.getInterpolatedHeight = function(factor) {
    var h1 = this.pp1.altitude;
    var h2 = this.pp2.altitude;
    return h1 + (h2 - h1) * factor;
};


MapTrajectory.prototype.getSineHeight = function(factor) {
    return this.getInterpolatedHeight(factor) +
           Math.sin(Math.PI * factor) * this.flightHeight;
};


MapTrajectory.prototype.getSmoothFactor = function(time) {
    var x = 0;
    var hds = this.headingDurationStart;
    var hde = this.headingDuration;

    if (time < hds) {
        x = 0;
    } else if (time > (this.duration - hde)) {
        x = 1.0;
    } else {
        x = Math.min(1.0, (time - hds) / (this.duration - hds - hde));
    }

    x = x*x*(3 - 2*x);
    return x*x*(3 - 2*x);
};


MapTrajectory.prototype.getFlightOrienation = function(time) {
    var o1 = null;
    var o2 = null;
    var fo = [0, -90, 0]; //flight orientation
    var factor = 0;

    //get fly direction angle
    fo[0] = this.azimuth % 360;

    if (fo[0] < 0) {
        fo[0] = 360 - Math.abs(fo[0]);
    }

    if (this.headingDurationStart > 0 && time <= this.headingDurationStart) { //start sequence
        factor = time / this.headingDurationStart;
        o1 = this.orientation(this.pp1);
        o2 = fo;
    } else if (time >= this.duration - this.headingDuration) { //end sequence
        factor = (time - (this.duration - this.headingDuration)) / this.headingDuration;
        o1 = fo;
        o2 = this.orientation(this.pp2);
    } else { //fly sequence
        factor = 0;
        o1 = fo;
        o2 = fo;
    }

    return this.getInterpolatedOrinetation(o1, o2, factor);
};


// A yaw wrapped into [0, 360).
function normalizeYaw(yaw) {
    return yaw < 0 ? (360 + (yaw % 360)) : (yaw % 360);
}


// A copy of a camera definition.
function copyDefinition(definition) {
    var copy = Object.assign({}, definition);
    copy.center = [definition.center[0], definition.center[1]];
    return copy;
}


export default MapTrajectory;
